import React, { useState, useEffect } from 'react';
import {
    Save,
    RefreshCw,
    MapPin,
    Truck,
    Globe,
    Shield,
    Bell,
    Palette,
    Database,
    Monitor,
    AlertTriangle,
    Sun,
    Moon
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const Settings = () => {
    // Use global theme context
    const { theme, setTheme, getThemeClasses } = useTheme();

    // General Settings
    const [generalSettings, setGeneralSettings] = useState({
        systemName: 'FracAdapt Fleet Management',
        timezone: 'UTC+0',
        language: 'English',
        dateFormat: 'MM/DD/YYYY',
        unitSystem: 'Metric', // 'Metric' (SI) or 'Imperial' (US Customary)
        autoSave: true,
        autoRefresh: 30, // seconds
    });

    // Map & Navigation Settings
    const [mapSettings, setMapSettings] = useState({
        defaultMapType: 'terrain',
        defaultZoom: 6,
        defaultCenter: { lat: 34.5553, lng: 69.2075 }, // Kabul, Afghanistan
        routeProfile: 'driving-car',
        showTrafficLayer: false,
        showWeatherOverlay: true
    });

    // Fleet Management Settings
    const [fleetSettings, setFleetSettings] = useState({
        maintenanceAlertThreshold: 70, // percentage
        criticalHealthThreshold: 50, // percentage
        averageSpeed: 80, // km/h - default average speed for calculations
        fuelConsumptionRate: 12, // L/100km
        serviceIntervalReminder: 7, // days before
        componentWearTracking: true,
        predictiveMaintenance: true,
    });

    // API & Integration Settings
    const [apiSettings, setApiSettings] = useState({
        weatherApiKey: '',
        routingService: 'openrouteservice', // openrouteservice, osrm
        weatherService: 'openweathermap',
        geocodingService: 'nominatim',
        apiTimeout: 10000, // milliseconds
        retryAttempts: 3,
        cacheEnabled: true,
        cacheDuration: 3600, // seconds
    });

    // Notification Settings
    const [notificationSettings, setNotificationSettings] = useState({
        enableNotifications: true,
        maintenanceAlerts: true,
        weatherWarnings: true,
        routeUpdates: true,
        systemAlerts: true,
        emailNotifications: false,
        soundEnabled: true,
        notificationFrequency: 'immediate', // immediate, hourly, daily
    });

    // Security Settings
    const [securitySettings, setSecuritySettings] = useState({
        sessionTimeout: 480, // minutes
        requirePasswordChange: false,
        twoFactorAuth: false,
        auditLogging: true,
        dataEncryption: true,
        backupFrequency: 'daily', // daily, weekly, monthly
        dataRetention: 365, // days
    });

    // Theme & Display Settings
    const [displaySettings, setDisplaySettings] = useState({
        theme: 'dark', // dark, light, auto
        accentColor: 'military-green',
        fontSize: 'medium', // small, medium, large
        compactMode: false,
        showAnimations: true,
        highContrast: false,
        reducedMotion: false,
    });

    // Load settings from localStorage on component mount
    useEffect(() => {
        const savedSettings = localStorage.getItem('fracadapt-settings');
        if (savedSettings) {
            try {
                const parsed = JSON.parse(savedSettings);
                setGeneralSettings(prev => ({ ...prev, ...parsed.general }));
                setMapSettings(prev => ({ ...prev, ...parsed.map }));
                setFleetSettings(prev => ({ ...prev, ...parsed.fleet }));
                setApiSettings(prev => ({ ...prev, ...parsed.api }));
                setNotificationSettings(prev => ({ ...prev, ...parsed.notifications }));
                setSecuritySettings(prev => ({ ...prev, ...parsed.security }));
                setDisplaySettings(prev => ({ ...prev, ...parsed.display }));

                // Set theme from saved settings
                if (parsed.display && parsed.display.theme) {
                    setTheme(parsed.display.theme);
                }
            } catch (error) {
                console.error('Failed to load settings:', error);
            }
        }
    }, []);

    // Save all settings to localStorage
    const handleSaveSettings = () => {
        const allSettings = {
            general: generalSettings,
            map: mapSettings,
            fleet: fleetSettings,
            api: apiSettings,
            notifications: notificationSettings,
            security: securitySettings,
            display: displaySettings,
            lastUpdated: new Date().toISOString(),
        };

        try {
            localStorage.setItem('fracadapt-settings', JSON.stringify(allSettings));
            alert('Settings saved successfully!');
        } catch (error) {
            console.error('Failed to save settings:', error);
            alert('Failed to save settings. Please try again.');
        }
    };

    // Reset settings to defaults
    const handleResetSettings = () => {
        if (window.confirm('Are you sure you want to reset all settings to defaults? This action cannot be undone.')) {
            localStorage.removeItem('fracadapt-settings');
            window.location.reload();
        }
    };

    // Test API connection
    const handleTestApiConnection = async () => {
        alert('API connection test feature coming soon...');
    };

    // Handle theme change
    const handleThemeChange = (newTheme) => {
        setDisplaySettings(prev => ({ ...prev, theme: newTheme }));
        setTheme(newTheme);
    };

    const themeClasses = getThemeClasses();

    return (
        <div className={`min-h-screen ${themeClasses.primary} overflow-x-hidden`}>
            <div className="p-3 sm:p-4 md:p-6 space-y-6 sm:space-y-8 max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className={`text-3xl font-bold ${themeClasses.textPrimary} mb-2`}>System Settings</h1>
                        <p className={themeClasses.textMuted}>Configure FracAdapt Fleet Management System</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={handleResetSettings}
                            className={`flex items-center gap-2 px-4 py-2 ${themeClasses.buttonDanger} rounded-lg transition-colors`}
                        >
                            <RefreshCw size={16} />
                            Reset to Defaults
                        </button>
                        <button
                            onClick={handleSaveSettings}
                            className={`flex items-center gap-2 px-6 py-2 ${themeClasses.buttonPrimary} rounded-lg transition-colors`}
                        >
                            <Save size={16} />
                            Save Settings
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Theme & Display Settings - Moved to top */}
                    <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-6`}>
                        <div className="flex items-center gap-3 mb-6">
                            <Palette className={themeClasses.textAccent} size={24} />
                            <h2 className={`text-xl font-semibold ${themeClasses.textPrimary}`}>Theme & Display</h2>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-3`}>Theme Mode</label>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => handleThemeChange('dark')}
                                        className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${theme === 'dark'
                                            ? `${themeClasses.buttonPrimary} border-military-500`
                                            : `${themeClasses.card} ${themeClasses.border} ${themeClasses.textSecondary} hover:${themeClasses.borderInput}`
                                            }`}
                                    >
                                        <Moon size={18} />
                                        <span className="font-medium">Dark</span>
                                    </button>
                                    <button
                                        onClick={() => handleThemeChange('light')}
                                        className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${theme === 'light'
                                            ? `${themeClasses.buttonPrimary} border-military-500`
                                            : `${themeClasses.card} ${themeClasses.border} ${themeClasses.textSecondary} hover:${themeClasses.borderInput}`
                                            }`}
                                    >
                                        <Sun size={18} />
                                        <span className="font-medium">Light</span>
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>Font Size</label>
                                <select
                                    value={displaySettings.fontSize}
                                    onChange={(e) => setDisplaySettings(prev => ({ ...prev, fontSize: e.target.value }))}
                                    className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                                >
                                    <option value="small">Small</option>
                                    <option value="medium">Medium</option>
                                    <option value="large">Large</option>
                                </select>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${themeClasses.textSecondary}`}>Compact Mode</span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={displaySettings.compactMode}
                                            onChange={(e) => setDisplaySettings(prev => ({ ...prev, compactMode: e.target.checked }))}
                                            className="sr-only peer"
                                        />
                                        <div className={`w-11 h-6 ${themeClasses.toggleBg} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white ${themeClasses.toggleThumb} after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:rounded-full after:h-5 after:w-5 after:transition-all ${themeClasses.toggleActive}`}></div>
                                    </label>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${themeClasses.textSecondary}`}>Show Animations</span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={displaySettings.showAnimations}
                                            onChange={(e) => setDisplaySettings(prev => ({ ...prev, showAnimations: e.target.checked }))}
                                            className="sr-only peer"
                                        />
                                        <div className={`w-11 h-6 ${themeClasses.toggleBg} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white ${themeClasses.toggleThumb} after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:rounded-full after:h-5 after:w-5 after:transition-all ${themeClasses.toggleActive}`}></div>
                                    </label>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${themeClasses.textSecondary}`}>High Contrast</span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={displaySettings.highContrast}
                                            onChange={(e) => setDisplaySettings(prev => ({ ...prev, highContrast: e.target.checked }))}
                                            className="sr-only peer"
                                        />
                                        <div className={`w-11 h-6 ${themeClasses.toggleBg} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white ${themeClasses.toggleThumb} after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:rounded-full after:h-5 after:w-5 after:transition-all ${themeClasses.toggleActive}`}></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* General Settings */}
                    <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-6`}>
                        <div className="flex items-center gap-3 mb-6">
                            <Monitor className={themeClasses.textAccent} size={24} />
                            <h2 className={`text-xl font-semibold ${themeClasses.textPrimary}`}>General Settings</h2>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>System Name</label>
                                <input
                                    type="text"
                                    value={generalSettings.systemName}
                                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, systemName: e.target.value }))}
                                    className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>Timezone</label>
                                    <select
                                        value={generalSettings.timezone}
                                        onChange={(e) => setGeneralSettings(prev => ({ ...prev, timezone: e.target.value }))}
                                        className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                                    >
                                        <option value="UTC+0">UTC+0 (GMT)</option>
                                        <option value="UTC+4:30">UTC+4:30 (Afghanistan)</option>
                                        <option value="UTC+5">UTC+5 (Pakistan)</option>
                                        <option value="UTC-5">UTC-5 (EST)</option>
                                        <option value="UTC-8">UTC-8 (PST)</option>
                                    </select>
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>Language</label>
                                    <select
                                        value={generalSettings.language}
                                        onChange={(e) => setGeneralSettings(prev => ({ ...prev, language: e.target.value }))}
                                        className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                                    >
                                        <option value="English">English</option>
                                        <option value="Dari">Dari</option>
                                        <option value="Pashto">Pashto</option>
                                        <option value="Urdu">Urdu</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>Unit System</label>
                                <select
                                    value={generalSettings.unitSystem}
                                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, unitSystem: e.target.value }))}
                                    className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                                >
                                    <option value="Metric">Metric System (SI) - km, km/h, °C, kg</option>
                                    <option value="Imperial">US Customary System - mi, mph, °F, lbs</option>
                                </select>
                                <p className={`text-xs ${themeClasses.textMuted} mt-2`}>
                                    {generalSettings.unitSystem === 'Metric' 
                                        ? 'Using: kilometers, km/h, Celsius, kilograms' 
                                        : 'Using: miles, mph, Fahrenheit, pounds'}
                                </p>
                            </div>

                            <div className="flex items-center justify-between">
                                <span className={`text-sm font-medium ${themeClasses.textSecondary}`}>Auto-save enabled</span>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={generalSettings.autoSave}
                                        onChange={(e) => setGeneralSettings(prev => ({ ...prev, autoSave: e.target.checked }))}
                                        className="sr-only peer"
                                    />
                                    <div className={`w-11 h-6 ${themeClasses.toggleBg} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white ${themeClasses.toggleThumb} after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:rounded-full after:h-5 after:w-5 after:transition-all ${themeClasses.toggleActive}`}></div>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Map & Navigation Settings */}
                    <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-6`}>
                        <div className="flex items-center gap-3 mb-6">
                            <MapPin className={themeClasses.textAccent} size={24} />
                            <h2 className={`text-xl font-semibold ${themeClasses.textPrimary}`}>Map & Navigation</h2>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>Default Map Type</label>
                                <select
                                    value={mapSettings.defaultMapType}
                                    onChange={(e) => setMapSettings(prev => ({ ...prev, defaultMapType: e.target.value }))}
                                    className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                                >
                                    <option value="terrain">Terrain</option>
                                    <option value="satellite">Satellite</option>
                                    <option value="topography">Topography</option>
                                </select>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${themeClasses.textSecondary}`}>Show Weather Overlay</span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={mapSettings.showWeatherOverlay}
                                            onChange={(e) => setMapSettings(prev => ({ ...prev, showWeatherOverlay: e.target.checked }))}
                                            className="sr-only peer"
                                        />
                                        <div className={`w-11 h-6 ${themeClasses.toggleBg} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white ${themeClasses.toggleThumb} after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:rounded-full after:h-5 after:w-5 after:transition-all ${themeClasses.toggleActive}`}></div>
                                    </label>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${themeClasses.textSecondary}`}>Show Traffic Layer</span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={mapSettings.showTrafficLayer}
                                            onChange={(e) => setMapSettings(prev => ({ ...prev, showTrafficLayer: e.target.checked }))}
                                            className="sr-only peer"
                                        />
                                        <div className={`w-11 h-6 ${themeClasses.toggleBg} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white ${themeClasses.toggleThumb} after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:rounded-full after:h-5 after:w-5 after:transition-all ${themeClasses.toggleActive}`}></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Fleet Management Settings */}
                    <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-6`}>
                        <div className="flex items-center gap-3 mb-6">
                            <Truck className={themeClasses.textAccent} size={24} />
                            <h2 className={`text-xl font-semibold ${themeClasses.textPrimary}`}>Fleet Management</h2>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                                    Maintenance Alert Threshold ({fleetSettings.maintenanceAlertThreshold}%)
                                </label>
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={fleetSettings.maintenanceAlertThreshold}
                                    onChange={(e) => setFleetSettings(prev => ({ ...prev, maintenanceAlertThreshold: parseInt(e.target.value) }))}
                                    className={`w-full h-2 ${themeClasses.sliderBg} rounded-lg appearance-none cursor-pointer slider`}
                                />
                            </div>

                            <div>
                                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                                    Critical Health Threshold ({fleetSettings.criticalHealthThreshold}%)
                                </label>
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={fleetSettings.criticalHealthThreshold}
                                    onChange={(e) => setFleetSettings(prev => ({ ...prev, criticalHealthThreshold: parseInt(e.target.value) }))}
                                    className={`w-full h-2 ${themeClasses.sliderBg} rounded-lg appearance-none cursor-pointer slider`}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                                        Average Speed ({generalSettings.unitSystem === 'Metric' ? 'km/h' : 'mph'})
                                    </label>
                                    <input
                                        type="number"
                                        min="20"
                                        max="120"
                                        step="5"
                                        value={fleetSettings.averageSpeed}
                                        onChange={(e) => setFleetSettings(prev => ({ ...prev, averageSpeed: parseInt(e.target.value) }))}
                                        className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                                    />
                                    <p className={`text-xs ${themeClasses.textMuted} mt-1`}>
                                        {generalSettings.unitSystem === 'Metric' ? 'Enter speed in km/h' : 'Enter speed in mph'}
                                    </p>
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                                        Fuel Consumption Rate ({generalSettings.unitSystem === 'Metric' ? 'L/100km' : 'mpg'})
                                    </label>
                                    <input
                                        type="number"
                                        min="5"
                                        max="50"
                                        step="0.1"
                                        value={fleetSettings.fuelConsumptionRate}
                                        onChange={(e) => setFleetSettings(prev => ({ ...prev, fuelConsumptionRate: parseFloat(e.target.value) }))}
                                        className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                                    />
                                    <p className={`text-xs ${themeClasses.textMuted} mt-1`}>
                                        {generalSettings.unitSystem === 'Metric' 
                                            ? 'Liters per 100 kilometers' 
                                            : 'Miles per gallon (US)'}
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${themeClasses.textSecondary}`}>Component Wear Tracking</span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={fleetSettings.componentWearTracking}
                                            onChange={(e) => setFleetSettings(prev => ({ ...prev, componentWearTracking: e.target.checked }))}
                                            className="sr-only peer"
                                        />
                                        <div className={`w-11 h-6 ${themeClasses.toggleBg} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white ${themeClasses.toggleThumb} after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:rounded-full after:h-5 after:w-5 after:transition-all ${themeClasses.toggleActive}`}></div>
                                    </label>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${themeClasses.textSecondary}`}>Predictive Maintenance</span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={fleetSettings.predictiveMaintenance}
                                            onChange={(e) => setFleetSettings(prev => ({ ...prev, predictiveMaintenance: e.target.checked }))}
                                            className="sr-only peer"
                                        />
                                        <div className={`w-11 h-6 ${themeClasses.toggleBg} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white ${themeClasses.toggleThumb} after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:rounded-full after:h-5 after:w-5 after:transition-all ${themeClasses.toggleActive}`}></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* API & Integration Settings */}
                    <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-6`}>
                        <div className="flex items-center gap-3 mb-6">
                            <Globe className={themeClasses.textAccent} size={24} />
                            <h2 className={`text-xl font-semibold ${themeClasses.textPrimary}`}>API & Integration</h2>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>Weather API Key</label>
                                <div className="flex gap-2">
                                    <input
                                        type="password"
                                        value={apiSettings.weatherApiKey}
                                        onChange={(e) => setApiSettings(prev => ({ ...prev, weatherApiKey: e.target.value }))}
                                        placeholder="Enter OpenWeatherMap API key"
                                        className={`flex-1 px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                                    />
                                    <button
                                        onClick={handleTestApiConnection}
                                        className={`px-4 py-2 ${themeClasses.buttonPrimary} rounded-lg transition-colors`}
                                    >
                                        Test
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>Routing Service</label>
                                    <select
                                        value={apiSettings.routingService}
                                        onChange={(e) => setApiSettings(prev => ({ ...prev, routingService: e.target.value }))}
                                        className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                                    >
                                        <option value="openrouteservice">OpenRouteService</option>
                                        <option value="osrm">OSRM</option>
                                    </select>
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>Weather Service</label>
                                    <select
                                        value={apiSettings.weatherService}
                                        onChange={(e) => setApiSettings(prev => ({ ...prev, weatherService: e.target.value }))}
                                        className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                                    >
                                        <option value="openweathermap">OpenWeatherMap</option>
                                        <option value="weatherapi">WeatherAPI</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>API Timeout (ms)</label>
                                    <input
                                        type="number"
                                        min="1000"
                                        max="30000"
                                        step="1000"
                                        value={apiSettings.apiTimeout}
                                        onChange={(e) => setApiSettings(prev => ({ ...prev, apiTimeout: parseInt(e.target.value) }))}
                                        className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                                    />
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>Retry Attempts</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={apiSettings.retryAttempts}
                                        onChange={(e) => setApiSettings(prev => ({ ...prev, retryAttempts: parseInt(e.target.value) }))}
                                        className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                                    />
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <span className={`text-sm font-medium ${themeClasses.textSecondary}`}>Enable API Caching</span>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={apiSettings.cacheEnabled}
                                        onChange={(e) => setApiSettings(prev => ({ ...prev, cacheEnabled: e.target.checked }))}
                                        className="sr-only peer"
                                    />
                                    <div className={`w-11 h-6 ${themeClasses.toggleBg} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white ${themeClasses.toggleThumb} after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:rounded-full after:h-5 after:w-5 after:transition-all ${themeClasses.toggleActive}`}></div>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Notification Settings */}
                    <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-6`}>
                        <div className="flex items-center gap-3 mb-6">
                            <Bell className={themeClasses.textAccent} size={24} />
                            <h2 className={`text-xl font-semibold ${themeClasses.textPrimary}`}>Notifications</h2>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>Notification Frequency</label>
                                <select
                                    value={notificationSettings.notificationFrequency}
                                    onChange={(e) => setNotificationSettings(prev => ({ ...prev, notificationFrequency: e.target.value }))}
                                    className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                                >
                                    <option value="immediate">Immediate</option>
                                    <option value="hourly">Hourly Summary</option>
                                    <option value="daily">Daily Summary</option>
                                </select>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${themeClasses.textSecondary}`}>Enable Notifications</span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={notificationSettings.enableNotifications}
                                            onChange={(e) => setNotificationSettings(prev => ({ ...prev, enableNotifications: e.target.checked }))}
                                            className="sr-only peer"
                                        />
                                        <div className={`w-11 h-6 ${themeClasses.toggleBg} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white ${themeClasses.toggleThumb} after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:rounded-full after:h-5 after:w-5 after:transition-all ${themeClasses.toggleActive}`}></div>
                                    </label>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${themeClasses.textSecondary}`}>Maintenance Alerts</span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={notificationSettings.maintenanceAlerts}
                                            onChange={(e) => setNotificationSettings(prev => ({ ...prev, maintenanceAlerts: e.target.checked }))}
                                            className="sr-only peer"
                                        />
                                        <div className={`w-11 h-6 ${themeClasses.toggleBg} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white ${themeClasses.toggleThumb} after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:rounded-full after:h-5 after:w-5 after:transition-all ${themeClasses.toggleActive}`}></div>
                                    </label>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${themeClasses.textSecondary}`}>Weather Warnings</span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={notificationSettings.weatherWarnings}
                                            onChange={(e) => setNotificationSettings(prev => ({ ...prev, weatherWarnings: e.target.checked }))}
                                            className="sr-only peer"
                                        />
                                        <div className={`w-11 h-6 ${themeClasses.toggleBg} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white ${themeClasses.toggleThumb} after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:rounded-full after:h-5 after:w-5 after:transition-all ${themeClasses.toggleActive}`}></div>
                                    </label>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${themeClasses.textSecondary}`}>Sound Enabled</span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={notificationSettings.soundEnabled}
                                            onChange={(e) => setNotificationSettings(prev => ({ ...prev, soundEnabled: e.target.checked }))}
                                            className="sr-only peer"
                                        />
                                        <div className={`w-11 h-6 ${themeClasses.toggleBg} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white ${themeClasses.toggleThumb} after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:rounded-full after:h-5 after:w-5 after:transition-all ${themeClasses.toggleActive}`}></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Security Settings */}
                    <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-6`}>
                        <div className="flex items-center gap-3 mb-6">
                            <Shield className={themeClasses.textAccent} size={24} />
                            <h2 className={`text-xl font-semibold ${themeClasses.textPrimary}`}>Security & Privacy</h2>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                                    Session Timeout ({securitySettings.sessionTimeout} minutes)
                                </label>
                                <input
                                    type="range"
                                    min="30"
                                    max="1440"
                                    step="30"
                                    value={securitySettings.sessionTimeout}
                                    onChange={(e) => setSecuritySettings(prev => ({ ...prev, sessionTimeout: parseInt(e.target.value) }))}
                                    className={`w-full h-2 ${themeClasses.sliderBg} rounded-lg appearance-none cursor-pointer slider`}
                                />
                            </div>

                            <div>
                                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>Data Retention (days)</label>
                                <input
                                    type="number"
                                    min="30"
                                    max="3650"
                                    step="30"
                                    value={securitySettings.dataRetention}
                                    onChange={(e) => setSecuritySettings(prev => ({ ...prev, dataRetention: parseInt(e.target.value) }))}
                                    className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                                />
                            </div>

                            <div>
                                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>Backup Frequency</label>
                                <select
                                    value={securitySettings.backupFrequency}
                                    onChange={(e) => setSecuritySettings(prev => ({ ...prev, backupFrequency: e.target.value }))}
                                    className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                                >
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                </select>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${themeClasses.textSecondary}`}>Audit Logging</span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={securitySettings.auditLogging}
                                            onChange={(e) => setSecuritySettings(prev => ({ ...prev, auditLogging: e.target.checked }))}
                                            className="sr-only peer"
                                        />
                                        <div className={`w-11 h-6 ${themeClasses.toggleBg} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white ${themeClasses.toggleThumb} after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:rounded-full after:h-5 after:w-5 after:transition-all ${themeClasses.toggleActive}`}></div>
                                    </label>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${themeClasses.textSecondary}`}>Data Encryption</span>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={securitySettings.dataEncryption}
                                            onChange={(e) => setSecuritySettings(prev => ({ ...prev, dataEncryption: e.target.checked }))}
                                            className="sr-only peer"
                                        />
                                        <div className={`w-11 h-6 ${themeClasses.toggleBg} peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white ${themeClasses.toggleThumb} after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:rounded-full after:h-5 after:w-5 after:transition-all ${themeClasses.toggleActive}`}></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* System Information */}
                <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-6`}>
                    <div className="flex items-center gap-3 mb-6">
                        <Database className={themeClasses.textAccent} size={24} />
                        <h2 className={`text-xl font-semibold ${themeClasses.textPrimary}`}>System Information</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="text-center">
                            <div className={`text-2xl font-bold ${themeClasses.textAccent} mb-1`}>v1.0.0</div>
                            <div className={`text-sm ${themeClasses.textMuted}`}>System Version</div>
                        </div>
                        <div className="text-center">
                            <div className={`text-2xl font-bold ${themeClasses.textAccent} mb-1`}>2025</div>
                            <div className={`text-sm ${themeClasses.textMuted}`}>Build Year</div>
                        </div>
                        <div className="text-center">
                            <div className={`text-2xl font-bold ${themeClasses.textAccent} mb-1`}>Active</div>
                            <div className={`text-sm ${themeClasses.textMuted}`}>System Status</div>
                        </div>
                    </div>

                    <div className={`mt-6 p-4 ${themeClasses.warningBg} border ${themeClasses.warningBorder} rounded-lg`}>
                        <div className={`flex items-center gap-2 ${themeClasses.warningText} mb-2`}>
                            <AlertTriangle size={16} />
                            <span className="font-medium">Important Notice</span>
                        </div>
                        <p className={`text-sm ${themeClasses.textSecondary}`}>
                            Changes to API settings and security configurations may require system restart to take effect.
                            Always test configurations in a safe environment before deploying to production.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;
